import { useState } from "react";
import { ShieldCheck, Upload, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { appApi, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] || "");
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const FileField = ({ label, hint, value, onChange, testid }) => (
  <div>
    <label className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">{label}</label>
    <label
      data-testid={`${testid}-drop`}
      className="mt-2 flex items-center gap-3 border-2 border-dashed border-obsidian bg-paper px-4 py-3 text-sm cursor-pointer hover:bg-acid/30 transition-colors duration-150"
    >
      <Upload className="w-4 h-4 text-zinc-500 shrink-0" />
      <span className="truncate">{value ? value.name : hint}</span>
      <input
        type="file"
        accept="image/*,.pdf"
        className="hidden"
        data-testid={`${testid}-input`}
        onChange={(e) => onChange(e.target.files?.[0] || null)}
      />
    </label>
  </div>
);

export default function Kyc() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({ pan_number: "", aadhaar_number: "", account_holder_name: "", account_number: "", ifsc: "", bank_name: "" });
  const [files, setFiles] = useState({ pan: null, aadhaar: null, selfie: null });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setFile = (k) => (file) => setFiles((f) => ({ ...f, [k]: file }));

  const status = user?.kyc_status || "pending";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(form.pan_number.toUpperCase())) {
      setError("Invalid PAN format (e.g. ABCDE1234F).");
      return;
    }
    if (!/^\d{12}$/.test(form.aadhaar_number)) {
      setError("Aadhaar must be 12 digits.");
      return;
    }
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(form.ifsc.toUpperCase())) {
      setError("Enter a valid IFSC code (e.g. HDFC0001234).");
      return;
    }
    if (!files.pan || !files.aadhaar || !files.selfie) {
      setError("PAN image, Aadhaar image and selfie are all required.");
      return;
    }
    setBusy(true);
    try {
      const [panB64, aadhaarB64, selfieB64] = await Promise.all([
        toBase64(files.pan), toBase64(files.aadhaar), toBase64(files.selfie),
      ]);
      await appApi.post("kyc/submit", {
        pan_number: form.pan_number.toUpperCase(),
        pan_image_base64: panB64,
        aadhaar_number: form.aadhaar_number,
        aadhaar_image_base64: aadhaarB64,
        selfie_image_base64: selfieB64,
        bank: {
          account_holder_name: form.account_holder_name,
          account_number: form.account_number,
          ifsc: form.ifsc.toUpperCase(),
          bank_name: form.bank_name,
        },
      });
      toast.success("KYC submitted — our team will review it shortly.");
      await refreshUser();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="kyc-page" className="max-w-3xl space-y-6">
      <div>
        <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">KYC</p>
        <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">Verify your identity</h1>
        <p className="text-sm text-zinc-500 mt-2">PAN + Aadhaar + bank details unlock COD payouts and full booking limits.</p>
      </div>

      <div className="border-2 border-obsidian bg-white px-6 py-4 flex items-center justify-between" data-testid="kyc-status-card">
        <span className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Current status</span>
        <StatusBadge status={status} />
      </div>

      {status === "approved" ? (
        <div className="border-2 border-obsidian bg-acid p-8 flex items-center gap-4 text-white" data-testid="kyc-approved-card">
          <CheckCircle2 className="w-8 h-8 shrink-0" />
          <div>
            <p className="font-display font-extrabold text-xl tracking-tight">KYC approved</p>
            <p className="text-sm text-white/80">You're fully verified — COD payouts flow to your registered bank account.</p>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="border-2 border-obsidian bg-white p-6 space-y-8" data-testid="kyc-form">
          {error && <div data-testid="kyc-error" className="border-2 border-red-600 bg-red-50 text-red-700 text-sm font-semibold px-4 py-3">{error}</div>}
          {user?.kyc_rejection_reason && (
            <div className="border-2 border-red-600 bg-red-50 text-red-700 text-sm font-semibold px-4 py-3" data-testid="kyc-rejection-reason">
              Previously rejected: {user.kyc_rejection_reason}
            </div>
          )}

          <div>
            <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-blaze" /> Identity documents
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div>
                <label htmlFor="kyc-pan" className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">PAN number</label>
                <input id="kyc-pan" data-testid="kyc-pan-input" required maxLength={10} value={form.pan_number} onChange={set("pan_number")} placeholder="ABCDE1234F"
                  className="mt-2 w-full border-2 border-obsidian bg-paper px-4 py-2.5 text-sm uppercase outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
              </div>
              <div>
                <label htmlFor="kyc-aadhaar" className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Aadhaar number</label>
                <input id="kyc-aadhaar" data-testid="kyc-aadhaar-input" required maxLength={12} value={form.aadhaar_number} onChange={set("aadhaar_number")} placeholder="12 digits"
                  className="mt-2 w-full border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
              </div>
              <FileField label="PAN card image" hint="Upload PAN photo/PDF" value={files.pan} onChange={setFile("pan")} testid="kyc-pan-file" />
              <FileField label="Aadhaar image" hint="Upload Aadhaar photo/PDF" value={files.aadhaar} onChange={setFile("aadhaar")} testid="kyc-aadhaar-file" />
              <FileField label="Selfie" hint="Upload a clear selfie" value={files.selfie} onChange={setFile("selfie")} testid="kyc-selfie-file" />
            </div>
          </div>

          <div>
            <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Bank details (COD payouts)</p>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              {[
                { k: "account_holder_name", label: "Account holder name", ph: "As per bank records", testid: "kyc-bank-holder" },
                { k: "account_number", label: "Account number", ph: "Digits only", testid: "kyc-bank-account" },
                { k: "ifsc", label: "IFSC", ph: "HDFC0001234", testid: "kyc-bank-ifsc", max: 11 },
                { k: "bank_name", label: "Bank name", ph: "HDFC Bank", testid: "kyc-bank-name" },
              ].map((f) => (
                <div key={f.k}>
                  <label htmlFor={f.testid} className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">{f.label}</label>
                  <input id={f.testid} data-testid={`${f.testid}-input`} required maxLength={f.max} value={form[f.k]} onChange={set(f.k)} placeholder={f.ph}
                    className="mt-2 w-full border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
                </div>
              ))}
            </div>
          </div>

          <button type="submit" disabled={busy} data-testid="kyc-submit-button"
            className="bg-obsidian text-sky-300 font-bold px-8 py-3.5 border-2 border-obsidian shadow-[4px_4px_0px_0px_rgba(10,10,10,0.35)] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150 disabled:opacity-60">
            {busy ? "Submitting…" : "Submit KYC"}
          </button>
        </form>
      )}
    </div>
  );
}
